import { FlightItinerary } from '../../../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { OfferRequest } from '../../../../redux/states/offerRequest/getPublicOffers/getPublicOffers.types'
import date from '../../../../shared/functions/Date/date.functions'
import status from '../../../../shared/functions/Status/status.dictionaries'
import { AccepOfferValidationResult, RequiredCountries } from './OfferItem.types'

export function formatArrayWithAnd(arr: string[]): string {
  if (arr.length === 0) return ''
  if (arr.length === 1) return arr[0]
  if (arr.length === 2) return arr.join(' and ')

  return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`
}

export const validateOfferAcceptance = (
  flightItineraries: FlightItinerary[],
  items: { country: { iataCode: string; name: string } }[],
  deliveryLocation: { countryCode: string; country: string; city: string; cityIataCode: string },
  offer: OfferRequest
): AccepOfferValidationResult => {
  const item = items[0] // Since all items have the same country, we can use the first item's country
  const itemCountry = item.country
  const matchingFlightItineraries: FlightItinerary[] = []

  flightItineraries = flightItineraries.filter((flightItinerary) => {
    if (flightItinerary.itinerary.itinerarySource === 'external') {
      return (
        date(flightItinerary.itinerary.legs[0].travelDate).isSameOrAfter(date().currentDate) &&
        flightItinerary.status.name !== status.externalFlightBookingStatus.externalFlightBookingDetailsConfirmed
      )
    } else {
      return (
        date(flightItinerary.itinerary.legs[0].travelDate).isSameOrAfter(date().currentDate) &&
        flightItinerary.status.name !== status.flightStatus.flightBooked
      )
    }
  })

  for (const flightItinerary of flightItineraries) {
    const legs = flightItinerary.itinerary.legs
    let foundItemCountry = false
    let foundDeliveryCountryAndCity = false
    let correctDirection = false
    let arrivalDateValid = true

    // Track the sequence of countries and cities visited
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i]
      const originCountryCode = leg.origin.country.iataCode
      const destinationCountryCode = leg.destination.country.iataCode
      const destinationCityName = leg.destination.city.name

      // Check if this leg starts from item country
      if (originCountryCode === itemCountry.iataCode) {
        foundItemCountry = true
      }

      // Check if this leg arrives at delivery location
      if (destinationCountryCode === deliveryLocation.countryCode && destinationCityName === deliveryLocation.city) {
        foundDeliveryCountryAndCity = true

        // Check arrival date only if neededByDate exists
        if (offer.neededByDate && leg.travelDate) {
          // Compare dates as string values to avoid DateTime object issues
          const travelDateStr = date(leg.travelDate).format('yyyy-MM-dd')
          const neededByDateStr = date(offer.neededByDate).format('yyyy-MM-dd')

          if (travelDateStr && neededByDateStr && travelDateStr > neededByDateStr) {
            arrivalDateValid = false
          }
        }

        // Check if we found item country before finding delivery location
        if (foundItemCountry) {
          correctDirection = true
        }
      }
    }

    const isMatch = foundItemCountry && foundDeliveryCountryAndCity && correctDirection && arrivalDateValid

    if (isMatch) {
      matchingFlightItineraries.push(flightItinerary)
    }
  }

  const requiredCountries: RequiredCountries = {
    itemLocation: {
      iataCode: itemCountry.iataCode,
      name: itemCountry.name
    },
    deliveryLocation: {
      iataCode: deliveryLocation.countryCode,
      cityIataCode: deliveryLocation.cityIataCode,
      name: deliveryLocation.country,
      city: deliveryLocation.city,
      neededByDate: offer.neededByDate
    }
  }

  if (matchingFlightItineraries.length === 0) {
    return {
      multipleOptions: false,
      noMatchingFlightItineraries: true,
      requiredCountries,
      flightItineraries: []
    }
  } else if (matchingFlightItineraries.length > 1) {
    return {
      multipleOptions: true,
      noMatchingFlightItineraries: false,
      flightItineraries: matchingFlightItineraries,
      requiredCountries
    }
  } else {
    return {
      multipleOptions: false,
      noMatchingFlightItineraries: false,
      flightItineraries: matchingFlightItineraries,
      requiredCountries
    }
  }
}
